import { useMemo, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import { usePortalChat } from "./context";
import { VirtualizedList } from "./VirtualizedList";
import { useChatDirectory } from "../hooks/useChatDirectory";
import {
  PICKER_ROW_HEIGHT,
  GROUP_NAME_MAX_LENGTH,
  TOPIC_MAX_LENGTH,
  SECTION_NAME_MAX_LENGTH,
  SEARCH_MAX_LENGTH,
} from "../portalChat.constants";
import {
  createConversationRequest,
  createSectionRequest,
  renameSectionRequest,
  deleteSectionRequest,
  reorderSectionsRequest,
  updateProjectOrderRequest,
  type ChatConversation,
  type ChatDirectoryUser,
  type ChatSection,
} from "../api/portalChat.api";

const FIELD_CLASS =
  "h-10 w-full rounded-lg border border-[#DDE3E8] px-3 text-[0.84375rem] text-[#1C2228] outline-hidden focus:border-[#4794BA]";
const LABEL_CLASS = "mb-1.5 block text-xs font-semibold text-[#4A545C]";

/**
 * Pointer-based sortable rows (PRD §4.9: reorder is DRAG-ONLY and touch-drag must work — site
 * users are on tablets). Pointer events unify mouse and touch without a dependency.
 */
function useDragReorder(onDrop: (from: number, to: number) => void) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Returns the INSERTION slot (0..rows.length): a pointer below the last row's midpoint means
  // "insert at the end" — clamping to the last row index made every downward drop land one slot
  // below the indicator once the dragged row was removed from the array.
  const indexFromPointer = (clientY: number): number | null => {
    const container = containerRef.current;
    if (!container) return null;
    const rows = [...container.querySelectorAll<HTMLElement>("[data-drag-row]")];
    for (const [i, row] of rows.entries()) {
      const rect = row.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return i;
    }
    return rows.length ? rows.length : null;
  };

  const handleProps = (index: number) => ({
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragIndex(index);
      setOverIndex(index);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (dragIndex === null) return;
      const target = indexFromPointer(e.clientY);
      if (target !== null) setOverIndex(target);
    },
    onPointerUp: () => {
      if (dragIndex !== null && overIndex !== null) {
        // The insertion slot was computed BEFORE removal — dropping below the dragged row must
        // shift down by one after the splice-out, or every downward drop lands one row too far.
        const to = overIndex > dragIndex ? overIndex - 1 : overIndex;
        if (to !== dragIndex) onDrop(dragIndex, to);
      }
      setDragIndex(null);
      setOverIndex(null);
    },
    style: { touchAction: "none" as const, cursor: "grab" as const },
  });

  return { containerRef, handleProps, dragIndex, overIndex };
}

/** Row chrome shared by the two reorder dialogs: drop indicators above/below the hovered slot. */
function dragRowClass(index: number, dragIndex: number | null, overIndex: number | null): string {
  const dragging = dragIndex === index ? "opacity-50" : "";
  const above = overIndex === index && dragIndex !== null && dragIndex !== index ? "border-t-2 border-t-[#6BBBE3]" : "";
  const below =
    dragIndex !== null && overIndex !== null && index === overIndex - 1 && overIndex > dragIndex + 1
      ? "border-b-2 border-b-[#6BBBE3]"
      : "";
  return `${dragging} ${above} ${below}`;
}

/** Reorder the local copy of an id list; drops that leave the order unchanged are ignored. */
function moveId(ids: string[], from: number, to: number): string[] {
  const next = [...ids];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return ids;
  next.splice(to, 0, moved);
  return next;
}

/**
 * New chat (PRD §4.2): one people picker — one person starts a direct chat, several start a group
 * (name and topic appear once a second person is selected). Direct chats dedupe server-side.
 */
export function NewChatDialog({ onClose }: { readonly onClose: () => void }) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ChatDirectoryUser[]>([]);
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [creating, setCreating] = useState(false);
  const [duplicate, setDuplicate] = useState<ChatConversation | null>(null);
  // Server-side search + pagination — the whole portal directory never loads at once.
  const { users: filtered, loading, loadMore } = useChatDirectory(search);

  const isGroup = selected.length > 1;

  const toggleUser = (user: ChatDirectoryUser) => {
    setSelected((prev) =>
      prev.some((u) => String(u._id) === String(user._id))
        ? prev.filter((u) => String(u._id) !== String(user._id))
        : [...prev, user],
    );
  };

  const create = async (force = false) => {
    if (!selected.length || creating) return;
    setCreating(true);
    try {
      const response = await createConversationRequest({
        type: isGroup ? "group" : "direct",
        memberIds: selected.map((user) => user._id),
        ...(isGroup && name.trim() ? { name: name.trim() } : {}),
        ...(isGroup && topic.trim() ? { topic: topic.trim() } : {}),
        ...(force ? { force: true } : {}),
      });
      if (!response.data.status) {
        chat.showServerError(response.data.errorMessage, t("Could not create the chat"));
        return;
      }
      if (response.data.duplicateOf) {
        setDuplicate(response.data.duplicateOf);
        return;
      }
      const created = response.data.result;
      await chat.refreshList();
      if (created) chat.selectConversation(created._id);
      onClose();
    } finally {
      setCreating(false);
    }
  };

  if (duplicate) {
    return (
      <ModalStateDialogComponent
        isVisible
        width="26rem"
        headerTitle={t("Similar group exists")}
        onCancel={() => setDuplicate(null)}
        modelContent={
          <div className="text-sm">
            {t("A group with exactly these members already exists: {{name}}. Open it, or create a new one anyway?", {
              name: duplicate.name,
            })}
          </div>
        }
        footerContent={
          <div className="flex gap-2.5">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              onClick={() => void create(true)}
              disabled={creating}
            >
              {t("Create new")}
            </ButtonComponent>
            <ButtonComponent
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              onClick={() => {
                chat.selectConversation(duplicate._id);
                onClose();
              }}
            >
              {t("Open existing")}
            </ButtonComponent>
          </div>
        }
      />
    );
  }

  const namePlaceholder = selected.length
    ? selected.map((user) => user.fullName.split(" ")[0] ?? user.fullName).join(", ")
    : t("e.g. Structural coordination");
  const summary =
    selected.length === 0
      ? t("Select people to start")
      : selected.length === 1
        ? t("One person selected")
        : t("{{count}} people selected", { count: selected.length });

  return (
    <ModalStateDialogComponent
      isVisible
      width="30rem"
      headerTitle={t("New chat")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-3">
          <div className="text-xs leading-relaxed text-[#6B757D]">
            {isGroup
              ? t("Several people selected — this will start a group chat.")
              : t("Select one person for a direct message, or several for a group chat.")}
          </div>
          {isGroup && (
            <>
              <label className="block">
                <span className={LABEL_CLASS}>
                  {t("Conversation name")}{" "}
                  <span className="font-normal text-[#9AA5AD]">{t("— optional, members are used if left blank")}</span>
                </span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={GROUP_NAME_MAX_LENGTH}
                  placeholder={namePlaceholder}
                  className={FIELD_CLASS}
                />
              </label>
              <label className="block">
                <span className={LABEL_CLASS}>
                  {t("Topic")}{" "}
                  <span className="font-normal text-[#9AA5AD]">{t("— optional, shown under the name")}</span>
                </span>
                <input
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  maxLength={TOPIC_MAX_LENGTH}
                  placeholder={t("e.g. Rebar clash at the east grid")}
                  className={FIELD_CLASS}
                />
              </label>
            </>
          )}
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selected.map((user) => (
                <span
                  key={String(user._id)}
                  className="flex items-center gap-1.5 rounded-full bg-[#E4F1F9] py-0.5 pe-1.5 ps-2.5 text-xs font-semibold text-[#1F5F80]"
                >
                  {user.fullName}
                  <span
                    role="button"
                    tabIndex={0}
                    title={t("Remove")}
                    onClick={() => toggleUser(user)}
                    onKeyDown={() => undefined}
                    className="flex h-4 w-4 items-center justify-center rounded-full bg-white/70"
                  >
                    <IconComponent name="CrossIcon" width="0.45rem" height="0.45rem" />
                  </span>
                </span>
              ))}
            </div>
          )}
          <div className="relative">
            <span className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2">
              <IconComponent name="SearchIcon" width="0.9rem" height="0.9rem" />
            </span>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxLength={SEARCH_MAX_LENGTH}
              placeholder={t("Search by name or email")}
              className={`${FIELD_CLASS} ps-9`}
            />
          </div>
          <div className="h-64">
            {loading && !filtered.length && <div className="p-3 text-sm text-[#9AA5AD]">{t("Loading")}…</div>}
            {!loading && !filtered.length && (
              <div className="p-3 text-sm text-[#9AA5AD]">{t("No members match your search.")}</div>
            )}
            {filtered.length > 0 && (
              <VirtualizedList
                items={filtered}
                getKey={(user) => String(user._id)}
                rowHeight={PICKER_ROW_HEIGHT}
                onEndReached={loadMore}
                ariaLabel={t("Search by name or email")}
                renderRow={(user) => {
                  const isSelected = selected.some((u) => String(u._id) === String(user._id));
                  return (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleUser(user)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") toggleUser(user);
                      }}
                      className={`flex h-full w-full cursor-pointer items-center gap-2 rounded-lg px-1.5 text-start hover:bg-[#F7F9FB] ${
                        isSelected ? "bg-[#F4FAFD]" : ""
                      }`}
                    >
                      {/* The row toggles too — stop the checkbox click from bubbling into a second toggle. */}
                      <span
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        role="presentation"
                      >
                        <CheckBoxComponent checked={isSelected} onChange={() => toggleUser(user)} />
                      </span>
                      <UserNameTagComponent showAvatarOnly id={String(user._id)} size="XS" isCircle />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[0.84375rem] font-semibold text-[#1C2228]">
                          {user.fullName}
                        </span>
                        <span className="block truncate text-[0.7rem] text-[#6B757D]">
                          {user.designation ?? user.email}
                          {user.organisationName ? ` · ${user.organisationName}` : ""}
                        </span>
                      </span>
                    </div>
                  );
                }}
              />
            )}
          </div>
        </div>
      }
      footerContent={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="text-xs text-[#6B757D]">{summary}</span>
          <div className="flex gap-2.5">
            <ButtonComponent
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              onClick={() => void create()}
              disabled={!selected.length || creating}
            >
              {t("Start chat")}
            </ButtonComponent>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
              {t("Cancel")}
            </ButtonComponent>
          </div>
        </div>
      }
    />
  );
}

/**
 * Manage sections (PRD §4.9) for ONE category: Create, drag-only reorder (applied on Confirm),
 * Rename (opens the section dialog) and delete. Sections are private to the user.
 */
export function SectionsDialog({
  scope,
  onClose,
  onCreate,
  onRename,
}: {
  readonly scope: "direct" | "group";
  readonly onClose: () => void;
  readonly onCreate: () => void;
  readonly onRename: (section: ChatSection) => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [busy, setBusy] = useState(false);
  // Local order until Confirm; null = follow the server order.
  const [orderIds, setOrderIds] = useState<string[] | null>(null);

  const sections = useMemo(
    () => chat.sections.filter((s) => s.scope === scope).sort((a, b) => a.order - b.order),
    [chat.sections, scope],
  );
  // Sections created/deleted while the dialog is open (via the stacked section dialog) are
  // reconciled into the pending order: known ids keep their slot, new ones append.
  const ordered = useMemo(() => {
    if (!orderIds) return sections;
    const byId = new Map(sections.map((s) => [String(s._id), s]));
    const kept = orderIds.flatMap((id) => {
      const section = byId.get(id);
      return section ? [section] : [];
    });
    const missing = sections.filter((s) => !orderIds.includes(String(s._id)));
    return [...kept, ...missing];
  }, [sections, orderIds]);
  const dirty = ordered.some((section, index) => String(sections[index]?._id ?? "") !== String(section._id));

  // PRD §4.9: counts reflect VISIBLE chats only — hidden chats still belong but are not counted.
  const chatCount = (sectionId: string) =>
    chat.conversations.filter((c) => !c.hidden && String(c.sectionId ?? "") === sectionId).length;

  const { containerRef, handleProps, dragIndex, overIndex } = useDragReorder((from, to) => {
    setOrderIds(
      moveId(
        ordered.map((s) => String(s._id)),
        from,
        to,
      ),
    );
  });

  const remove = async (section: ChatSection) => {
    if (busy) return;
    setBusy(true);
    try {
      // PRD §4.9: deleting a section un-assigns its chats; it never deletes the chats.
      await deleteSectionRequest({ sectionId: section._id });
      chat.showToast(t("Section deleted"));
      await chat.refreshList();
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (busy) return;
    if (!dirty) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const response = await reorderSectionsRequest({ scope, sectionIds: ordered.map((s) => s._id) });
      if (response.data.status) {
        await chat.refreshList();
        onClose();
      } else {
        chat.showServerError(response.data.errorMessage, t("Something went wrong"));
      }
    } finally {
      setBusy(false);
    }
  };

  const typeLabel = scope === "direct" ? t("Direct Chats") : t("Group Chats");

  return (
    <ModalStateDialogComponent
      isVisible
      width="30rem"
      headerTitle={t("Manage sections")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-[#6B757D]">
              {t("{{type}} — order, rename or remove", { type: typeLabel })}
            </span>
            <button
              type="button"
              onClick={onCreate}
              className="flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-[#F4FAFD] px-3 text-xs font-semibold text-[#1C2228] hover:bg-[#E4F1F9]"
            >
              <IconComponent name="plus-icon" width="0.8rem" height="0.8rem" />
              {t("Create")}
            </button>
          </div>
          <div ref={containerRef} className="flex max-h-[45vh] min-h-32 flex-col gap-1.5 overflow-y-auto">
            {!ordered.length && (
              <div className="flex flex-col items-center gap-1 rounded-[0.625rem] border border-dashed border-[#C3CCD3] bg-[#F7F9FB] p-6 text-center">
                <span className="text-sm font-bold text-[#1C2228]">{t("No sections yet")}</span>
                <span className="text-xs leading-relaxed text-[#6B757D]">
                  {t("Create a section to group these chats — Priority, Handover, anything you like. Only you see it.")}
                </span>
              </div>
            )}
            {ordered.map((section, index) => (
              <div
                key={String(section._id)}
                data-drag-row
                className={`flex items-center gap-2.5 rounded-lg border border-[#EEF2F5] bg-white px-2.5 py-2 ${dragRowClass(
                  index,
                  dragIndex,
                  overIndex,
                )}`}
              >
                <span
                  {...handleProps(index)}
                  title={t("Drag a section by its handle to reorder.")}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-[#EEF2F5]"
                >
                  <IconComponent name="Drag" width="0.9rem" height="0.9rem" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-[#1C2228]">{section.name}</span>
                  <span className="block text-[0.6875rem] text-[#6B757D]">
                    {t("{{count}} chats · {{position}} of {{total}}", {
                      count: chatCount(String(section._id)),
                      position: index + 1,
                      total: ordered.length,
                    })}
                  </span>
                </span>
                <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={() => onRename(section)}>
                  {t("Rename")}
                </ButtonComponent>
                <IconComponent
                  name="DeleteIcon"
                  width="0.9rem"
                  height="0.9rem"
                  toolTipContent={t("Delete section")}
                  onClick={() => void remove(section)}
                />
              </div>
            ))}
          </div>
          <div className="text-xs leading-relaxed text-[#6B757D]">
            {t(
              "Drag a section by its handle to reorder. Sections are private to you, and chats not in a section always sit above them.",
            )}
          </div>
        </div>
      }
      footerContent={
        <div className="flex gap-2.5">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={() => void confirm()} disabled={busy}>
            {t("Confirm")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/** Manage sections for Project Chats (PRD §4.10): drag-only reordering of the user's OTHER projects, applied on Confirm. */
export function ProjectOrderDialog({ onClose }: { readonly onClose: () => void }) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [busy, setBusy] = useState(false);
  const [orderIds, setOrderIds] = useState<string[] | null>(null);

  const projects = useMemo(() => {
    const byProject = new Map<string, { projectId: string; name: string; count: number }>();
    for (const c of chat.conversations) {
      if (c.type !== "space" || !c.projectId) continue;
      const key = String(c.projectId);
      if (key === chat.currentProjectId) continue; // the current project always sits pinned on top
      const entry = byProject.get(key) ?? { projectId: key, name: c.projectName ?? t("Project"), count: 0 };
      entry.count += 1;
      byProject.set(key, entry);
    }
    const rows = [...byProject.values()];
    rows.sort((a, b) => {
      const ia = chat.projectOrder.indexOf(a.projectId);
      const ib = chat.projectOrder.indexOf(b.projectId);
      if (ia !== -1 || ib !== -1) {
        return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
      }
      return a.name.localeCompare(b.name);
    });
    return rows;
  }, [chat.conversations, chat.projectOrder, chat.currentProjectId, t]);

  const ordered = useMemo(() => {
    if (!orderIds) return projects;
    const byId = new Map(projects.map((p) => [p.projectId, p]));
    const kept = orderIds.flatMap((id) => {
      const project = byId.get(id);
      return project ? [project] : [];
    });
    return [...kept, ...projects.filter((p) => !orderIds.includes(p.projectId))];
  }, [projects, orderIds]);
  const dirty = ordered.some((project, index) => projects[index]?.projectId !== project.projectId);

  const { containerRef, handleProps, dragIndex, overIndex } = useDragReorder((from, to) => {
    setOrderIds(
      moveId(
        ordered.map((p) => p.projectId),
        from,
        to,
      ),
    );
  });

  const confirm = async () => {
    if (busy) return;
    if (!dirty) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      const response = await updateProjectOrderRequest({
        projectIds: ordered.map((p) => toObjectIdString(p.projectId)),
      });
      if (response.data.status) {
        await chat.refreshList();
        onClose();
      } else {
        chat.showServerError(response.data.errorMessage, t("Something went wrong"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="30rem"
      headerTitle={t("Manage sections")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-3">
          <span className="text-xs text-[#6B757D]">
            {t("Project Chats — drag to set the order projects appear in")}
          </span>
          <div ref={containerRef} className="flex max-h-[45vh] min-h-32 flex-col gap-1.5 overflow-y-auto">
            {!ordered.length && (
              <div className="flex flex-col items-center gap-1 rounded-[0.625rem] border border-dashed border-[#C3CCD3] bg-[#F7F9FB] p-6 text-center">
                <span className="text-sm font-bold text-[#1C2228]">{t("No other projects yet")}</span>
                <span className="text-xs leading-relaxed text-[#6B757D]">
                  {t("Once you are on more than one project, their chats appear here and you can set the order.")}
                </span>
              </div>
            )}
            {ordered.map((project, index) => (
              <div
                key={project.projectId}
                data-drag-row
                className={`flex items-center gap-2.5 rounded-lg border border-[#EEF2F5] bg-white px-2.5 py-2 ${dragRowClass(
                  index,
                  dragIndex,
                  overIndex,
                )}`}
              >
                <span
                  {...handleProps(index)}
                  title={t("Drag a section by its handle to reorder.")}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md hover:bg-[#EEF2F5]"
                >
                  <IconComponent name="Drag" width="0.9rem" height="0.9rem" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-[#1C2228]">{project.name}</span>
                  <span className="block text-[0.6875rem] text-[#6B757D]">
                    {t("{{count}} chats · {{position}} of {{total}}", {
                      count: project.count,
                      position: index + 1,
                      total: ordered.length,
                    })}
                  </span>
                </span>
              </div>
            ))}
          </div>
          <div className="text-xs leading-relaxed text-[#6B757D]">
            {t(
              "Your current project always sits at the top as Current Project. The rest follow the order you set here.",
            )}
          </div>
        </div>
      }
      footerContent={
        <div className="flex gap-2.5">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={() => void confirm()} disabled={busy}>
            {t("Confirm")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/** What the section dialog edits: a new section in `scope` (optionally filing a chat into it), or a rename. */
export type SectionDialogTarget = {
  scope: "direct" | "group";
  section?: ChatSection | undefined;
  assignConversationId?: string | undefined;
};

/**
 * New / Rename section dialog (PRD §4.9): the category chip, one required name, a "How sections
 * work" note, Create (or Save) disabled while empty. When opened from a chat row's Move-to-section
 * menu the fresh section immediately receives that chat.
 */
export function SectionDialog({
  target,
  onClose,
}: {
  readonly target: SectionDialogTarget;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const editing = target.section;
  const [name, setName] = useState(editing?.name ?? "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      if (editing) {
        const response = await renameSectionRequest({
          sectionId: toObjectIdString(String(editing._id)),
          name: trimmed,
        });
        if (response.data.status) {
          await chat.refreshList();
          chat.showToast(t("Section renamed"));
          onClose();
        } else {
          chat.showServerError(response.data.errorMessage, t("Something went wrong"));
        }
        return;
      }
      const response = await createSectionRequest({ name: trimmed, scope: target.scope });
      if (response.data.status && response.data.result) {
        if (target.assignConversationId) {
          await chat.updateConversationState(target.assignConversationId, {
            sectionId: String(response.data.result._id),
          });
        }
        await chat.refreshList();
        chat.showToast(t("Section {{name}} created", { name: trimmed }));
        onClose();
      } else if (!response.data.status) {
        chat.showServerError(response.data.errorMessage, t("Something went wrong"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="25rem"
      headerTitle={editing ? t("Rename section") : t("New section")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[0.6875rem] text-[#6B757D]">{t("Inside")}</span>
            <span className="rounded-full bg-[#E4F1F9] px-2.5 py-0.5 text-[0.6875rem] font-bold text-[#1F5F80]">
              {target.scope === "direct" ? t("Direct Chats") : t("Group Chats")}
            </span>
          </div>
          <label className="block">
            <span className={LABEL_CLASS}>{t("Section name")}</span>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={SECTION_NAME_MAX_LENGTH}
              placeholder={t("e.g. Priority, Handover, Design")}
              className={FIELD_CLASS}
              onKeyDown={(e) => {
                if (e.key === "Enter") void submit();
              }}
            />
          </label>
          <div className="rounded-lg border border-[#EEF2F5] bg-[#F7F9FB] px-3 py-2.5 text-[0.7rem] leading-relaxed text-[#6B757D]">
            <span className="mb-0.5 block font-bold text-[#4A545C]">{t("How sections work")}</span>
            {t(
              "Sections are private to you and live inside a chat category. Once created, open any chat's ••• menu in that category and pick the section to move it in.",
            )}
          </div>
        </div>
      }
      footerContent={
        <div className="flex gap-2.5">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            disabled={!name.trim() || busy}
            onClick={() => void submit()}
          >
            {editing ? t("Save") : t("Create")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/** Hide confirmation — title and per-type body copy from PRD §4.11. */
export function HideConfirmDialog({
  conversation,
  onClose,
}: {
  readonly conversation: ChatConversation;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();

  const body =
    conversation.type === "space"
      ? t(
          "{{name}} will be removed from your sidebar. It reappears in Current Project whenever you are working in {{projectName}}, and hides again when you move to another project.",
          { name: conversation.name, projectName: conversation.projectName ?? "" },
        )
      : t(
          "{{name}} will be removed from your sidebar. Your history stays intact and you remain a member. Search for it any time to bring it back, and it reappears automatically when someone sends a new message.",
          { name: conversation.name },
        );

  return (
    <ModalStateDialogComponent
      isVisible
      width="26rem"
      headerTitle={t("Hide this conversation?")}
      onCancel={onClose}
      modelContent={<div className="text-[0.84375rem] leading-relaxed text-[#4A545C]">{body}</div>}
      footerContent={
        <div className="flex gap-2.5">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={() => {
              void chat.updateConversationState(conversation._id, { hidden: true });
              chat.showToast(t("{{name}} hidden — search to bring it back.", { name: conversation.name }));
              // PRD §4.11: the panel falls to the next visible chat of the same type.
              if (chat.selectedId === conversation._id) chat.selectNextAfter(conversation._id);
              onClose();
            }}
          >
            {t("Hide conversation")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}
